import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/Button';
import { ButtonBack } from '../../components/ButtonBack';
import { Card } from '../../components/Card';
import { Input } from '../../components/forms/Input';
import { HelmetComponent } from '../../components/Helmet';
import { List, ListItem } from '../../components/List';
import { Notify, NotifyUpdate } from '../../components/Notify';
import { ModalCongratsRescue } from './components/ModalCongratsRescue';

import * as routes from '../../constants/Routes';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import { api } from '../../server/api';
import { MaskCNPJ, MaskCPF, maskTEL, maskCurrency } from '../../utils/mask';

import pixImage from '../../assets/images/pix.png';
import { usePoints } from '../../hooks/usePoints';
import { GANHE_MAIS_CORRETOR_CLIENT_URL } from '../../constants/ClientUrls';
import { validateCNPJ } from '../../utils/validate';
import Modal from 'react-responsive-modal';
import 'react-responsive-modal/styles.css'; // Adicione este import
import moment from 'moment';

interface FormValues {
  pixkey: string;
  name: string;
  cpf: string;
  birth: string;
  digitAgency: string;
  digitCount: string;
  bank: string;
  accountType: string;
  labelBank: string;
  agency: string;
  conta: string;
  cep: string;
  street: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  number: string;
  notaNumero: string;
  notaSerie: string;
  notaDataEmissao: string;
  notaValor: string;
  regimeTributacao: RegimeTributacao;
}

enum RegimeTributacao {
  'SIMPLES/MEI' = 'SIMPLES/MEI',
  'PRESUMIDO/REAL' = 'PRESUMIDO/REAL',
}
export interface ITaxInvoice {
  numero: string;
  serie: string;
  dataEmissao: string;
  valorTotal: number;
  regimeTributacao: RegimeTributacao;
  fileUrl: string;
}

export function ConfirmRescue() {
  const navigate = useNavigate();
  const { client, player } = useAuth();
  const { availableToRedeem } = usePoints();

  const {
    cart,
    isAwardMoney,
    totalPaymentValue,
    totalPremiumPrice,
    isAwardVariable,
    clearCart,
  } = useCart();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>();
  const selectedRegime = watch('regimeTributacao');
  const [typePix, setTypePix] = useState('');
  const [maxLength, setMaxLength] = useState<number | undefined>(undefined);
  const [modalCongratsIsOpen, setModalCongratsIsOpen] = useState(false);
  const [modalConfirmPixIsOpen, setModalConfirmPixIsOpen] = useState(false); // Novo estado
  const [isConfirmingRescue, setIsConfirmingRescue] = useState(false);
  const [formDataToSubmit, setFormDataToSubmit] = useState<FormValues | null>(
    null
  ); // Armazenar dados do form

  const [taxInvoiceFileUrl, setNotaFiscalFileUrl] = useState<string>('');
  const [taxInvoiceFileName, setNotaFiscalFileName] = useState<string>('');
  const [isUploadingNotaFiscal, setIsUploadingNotaFiscal] = useState(false);
  const [modalConfirmRegimeIsOpen, setModalConfirmRegimeIsOpen] =
    useState(false);

  const hasBs2Premium = cart.some(
    (item) =>
      item.premiumType.value === 'BS2' || item.premiumType.label === 'BS2'
  );

  const requiresTaxInvoice =
    client.settings?.requireTaxInvoice === true &&
    player.regional === 'sim' &&
    hasBs2Premium;

  const allowsPaymentToThirdParties =
    client.settings?.paymentToThirdParties === true;

  // Value the player is actually redeeming. For variable awards (e.g. ganhemaiscorretor)
  // the chosen amount lives in totalPaymentValue; fall back to availableToRedeem
  // for variable awards that don't carry a per-item paymentValue.
  const effectiveRescueValue = isAwardVariable
    ? Number(totalPaymentValue) || availableToRedeem
    : totalPremiumPrice;

  // Auto-fill the tax-invoice "Valor" with the redeemed amount; it must match
  // the rescue value exactly and is not editable by the player.
  useEffect(() => {
    const value = Number(effectiveRescueValue);
    if (value > 0) {
      setValue('notaValor', maskCurrency(String(Math.round(value * 100))));
    }
  }, [effectiveRescueValue, setValue]);

  const checkStock = async (
    deliverAddress: any,
    accountData: any,
    taxInvoice: ITaxInvoice | null
  ) => {
    const nId = Notify('Aguarde... registrando seu resgate...', 'loading');

    const parameters = {
      searchFunctionality: 'checkStockAward',
      userType: 'player',
      cart,
      total: client.rescueByGoals === 'true' ? 0 : effectiveRescueValue,
      totalPaymentValue: isAwardMoney
        ? isAwardVariable
          ? effectiveRescueValue
          : totalPaymentValue
        : 0,
      deliverAddress,
      accountData,
      clientId: client.id,
      ...(taxInvoice && { taxInvoice }),
    };

    const responseStockAward = await api.post('/search', parameters);

    if (responseStockAward.data.success === true) {
      NotifyUpdate(responseStockAward.data.message, 'success', nId);
      toggleModal();
    } else {
      NotifyUpdate(responseStockAward.data.message, 'error', nId);
    }
  };

  const handleForm = handleSubmit(async (formData) => {
    if (requiresTaxInvoice && !taxInvoiceFileUrl) {
      Notify('Por favor, anexe a nota fiscal em PDF', 'warn');
      return;
    }

    // Prêmios que não são dinheiro (físico/voucher) não têm chave Pix para
    // confirmar: finaliza o resgate direto, sem passar pelo modal
    if (!isAwardMoney) {
      await submitRescue(formData);
      return;
    }

    // Em vez de executar tudo aqui, apenas abre o modal
    setFormDataToSubmit(formData);
    setModalConfirmPixIsOpen(true);
  });

  const submitRescue = async (formData: FormValues) => {
    if (isConfirmingRescue) return;

    setIsConfirmingRescue(true);
    try {
      const isAwardVoucher = cart.some(
        (item) =>
          item.premiumType.value === 'digital' ||
          item.premiumType.value === 'Digital'
      );

      const {
        pixkey,
        cep,
        street,
        complement,
        neighborhood,
        city,
        state,
        number,
        notaNumero,
        notaSerie,
        notaDataEmissao,
        notaValor,
      } = formData;

      const deliverAddress = {
        cep,
        street,
        complement,
        neighborhood,
        city,
        state,
        number,
      };

      let accountData = {};

      if (isAwardMoney) {
        if (typePix === 'CPF' && !allowsPaymentToThirdParties) {
          if (player.cpf.replace(/\D/g, '') !== pixkey.replace(/\D/g, '')) {
            return Notify(`O cpf não corresponde`, 'warn');
          }
        }

        if (typePix === 'CNPJ' && !allowsPaymentToThirdParties) {
          if (player.cpf.replace(/\D/g, '') !== pixkey.replace(/\D/g, '')) {
            return Notify(`O cnpj não corresponde`, 'warn');
          }
        }

        accountData = {
          pixkey: pixkey,
          paymentMethod: 'pix',
          typePix,
        };

        const parameters = {
          updateFunctionality: 'updateAddressPlayer',
          userType: 'player',
          deliverAddress,
          accountData,
          clientId: client.id,
        };

        await api.post('update', parameters);
      }

      const taxInvoice: ITaxInvoice | null = requiresTaxInvoice
        ? {
            numero: notaNumero,
            serie: notaSerie,
            dataEmissao: notaDataEmissao,
            regimeTributacao: RegimeTributacao[selectedRegime],
            valorTotal:
              parseFloat(notaValor.replace(/\./g, '').replace(',', '.')) || 0,
            fileUrl: taxInvoiceFileUrl,
          }
        : null;

      await checkStock(deliverAddress, accountData, taxInvoice);

      if (isAwardVoucher) {
        clearCart();
        navigate(routes.MY_RESCUES);
      }

      setModalConfirmPixIsOpen(false);
    } catch (error) {
      console.log('erro submitRescue', error);
    } finally {
      setIsConfirmingRescue(false);
    }
  };

  // Confirma a chave Pix no modal e envia os dados
  const handleConfirmPix = async () => {
    if (!formDataToSubmit) return;

    await submitRescue(formDataToSubmit);
  };

  const handleCloseConfirmModal = () => {
    setModalConfirmPixIsOpen(false);
    setFormDataToSubmit(null);
  };

  const handleRegimeChange = (regime: string) => {
    if (regime === 'PRESUMIDO/REAL') {
      setModalConfirmRegimeIsOpen(true);
    } else {
      setValue('regimeTributacao', RegimeTributacao['SIMPLES/MEI']);
    }
  };

  const handleConfirmRegime = () => {
    setValue('regimeTributacao', RegimeTributacao['PRESUMIDO/REAL']);
    setModalConfirmRegimeIsOpen(false);
  };

  const handleCancelRegime = () => {
    setValue('regimeTributacao', RegimeTributacao['SIMPLES/MEI']);
    setModalConfirmRegimeIsOpen(false);
  };

  const handleNotaFiscalUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      Notify('Por favor, selecione um arquivo PDF', 'error');
      event.target.value = '';
      return;
    }

    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSizeInBytes) {
      Notify('O arquivo deve ter no máximo 5MB', 'error');
      event.target.value = '';
      return;
    }

    setIsUploadingNotaFiscal(true);
    const nId = Notify('Enviando nota fiscal...', 'loading');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/file-upload/tax-invoice', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.error === false) {
        setNotaFiscalFileUrl(response.data.body);
        setNotaFiscalFileName(file.name);
        NotifyUpdate('Nota fiscal enviada com sucesso!', 'success', nId);
      } else {
        NotifyUpdate('Erro ao enviar nota fiscal', 'error', nId);
        event.target.value = '';
      }
    } catch (error) {
      console.error('Erro ao enviar nota fiscal:', error);
      NotifyUpdate('Erro ao enviar nota fiscal', 'error', nId);
      event.target.value = '';
    } finally {
      setIsUploadingNotaFiscal(false);
    }
  };

  // Remove uploaded nota fiscal
  const handleRemoveNotaFiscal = () => {
    setNotaFiscalFileUrl('');
    setNotaFiscalFileName('');
  };

  // GanheMaisCorretor reaches this page straight from GanheMaisCorretorView, skipping the Cart page.
  // Going back lands on GanheMaisCorretorView, which blocks re-entry while a cart exists
  // ("esvaziar o carrinho"), so we must clear on the way out. Every other flow
  // (including variable awards for other clients) goes back to the Cart page,
  // which renders the existing cart — clearing there would wrongly empty it.
  const leaveRescue = () => {
    if (client?.clientUrl === GANHE_MAIS_CORRETOR_CLIENT_URL) {
      clearCart();
    }
  };

  const onHandleCancelRescue = () => {
    leaveRescue();
    navigate(routes.REDEEM_AWARDS);
  };

  const handleChange = (type: string) => {
    //929.324.090-42 cpf 14
    //77.996.783/0001-30 cnpj 18
    //(86) 99979-7405 telefone 15
    //2772b87c-b909-4e42-903b-4eab0ff57fff chave aleatória 32
    if (type === 'CPF') {
      setTypePix('CPF');
      setMaxLength(14);
      setValue('pixkey', '');
    }
    if (type === 'CNPJ') {
      setTypePix('CNPJ');
      setMaxLength(18);
      setValue('pixkey', '');
    }
    if (type === 'TELEFONE') {
      setTypePix('TELEFONE');
      setMaxLength(15);
      setValue('pixkey', '');
    }
    if (type === 'CHAVE_ALEATORIA') {
      setTypePix('CHAVE_ALEATORIA');
      setMaxLength(36);
      setValue('pixkey', '');
    }
    if (type === 'EMAIL') {
      setTypePix('EMAIL');
      setMaxLength(undefined);
      setValue('pixkey', '');
    }
  };

  const toggleModal = () => {
    setModalCongratsIsOpen(!modalCongratsIsOpen);
  };

  const renderEmptyCart = () => {
    return (
      <>
        <div className="container d-flex flex-column align-items-center my-5">
          {/* <img className="mr-5" src={noNotfyImage} width="300" alt="" /> */}
          <br />
          <div className="text-center">
            <h3>Ops...</h3>
            <p className="text-secondary mt-3">Seu carrinho está vazio!</p>
          </div>
        </div>
      </>
    );
  };

  const renderFormRescueMoney = () => {
    return (
      <form onSubmit={handleForm} noValidate>
        <div className="row">
          <div className="col-sm-12">
            <div className="mb-3">
              <Card>
                <>
                  {renderNotaFiscal()}
                  {renderPaymentPix()}

                  <div className="mt-3 d-flex flex-row-reverse">
                    <Button
                      type="submit"
                      text="Finalizar resgate"
                      color="primary"
                      icon="check"
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                    />
                    <div className="me-3">
                      <Button
                        type="button"
                        onClick={onHandleCancelRescue}
                        text="Cancelar"
                        color="flat"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </>
              </Card>
            </div>
          </div>
        </div>
      </form>
    );
  };

  const renderNotaFiscal = () => {
    if (!requiresTaxInvoice) return null;

    return (
      <div className="mb-4">
        <div
          className="mb-3"
          style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
        >
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: 'var(--primary-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i
              className="material-icons"
              style={{ fontSize: 14, color: '#fff' }}
            >
              description
            </i>
          </span>
          <span className="fs-4 fw-semibold">Nota Fiscal</span>
        </div>

        <div className="row">
          <div className="col-sm-12 col-md-6 mb-3">
            <Input
              type="text"
              label="Número da Nota *"
              placeholder="Ex: 12345"
              {...register('notaNumero', { required: true })}
              errors={errors.notaNumero ? 'Campo obrigatório' : ''}
            />
          </div>
          <div className="col-sm-12 col-md-6 mb-3">
            <Input
              type="text"
              label="Série *"
              placeholder="Ex: 1"
              {...register('notaSerie', { required: true })}
              errors={errors.notaSerie ? 'Campo obrigatório' : ''}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12 col-md-6 mb-3">
            <Input
              type="date"
              max={moment().format('YYYY-MM-DD')}
              label="Data de Emissão *"
              placeholder="dd/mm/yyyy"
              {...register('notaDataEmissao', {
                required: true,
                max: moment().format('YYYY-MM-DD'),
              })}
              errors={
                errors.notaDataEmissao?.type === 'required'
                  ? 'Campo obrigatório'
                  : errors.notaDataEmissao?.type === 'max'
                    ? 'Data de emissão não pode ser no futuro'
                    : ''
              }
            />
          </div>
          <div className="col-sm-12 col-md-6 mb-3">
            <Input
              type="text"
              label="Valor (R$) *"
              placeholder="Ex: 1.500,00"
              readOnly
              {...register('notaValor', {
                required: true,
                validate: (value) => {
                  if (!value) return 'Campo obrigatório';
                  const numeric = parseFloat(
                    String(value).replace(/\./g, '').replace(',', '.')
                  );
                  if (isNaN(numeric)) return 'Valor inválido';
                  if (Math.abs(numeric - Number(effectiveRescueValue)) > 0.005)
                    return 'O valor da nota fiscal deve ser igual ao valor total do resgate';
                  return true;
                },
              })}
              errors={
                errors.notaValor?.type === 'required'
                  ? 'Campo obrigatório'
                  : errors.notaValor?.type === 'validate'
                    ? errors.notaValor.message
                    : ''
              }
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12 mb-3">
            <label className="form-label">Regime Tributário *</label>
            <div className="row">
              <div className="col-6">
                <input
                  type="radio"
                  id="regime-mei-simples"
                  value="SIMPLES/MEI"
                  {...register('regimeTributacao', { required: true })}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="regime-mei-simples"
                  onClick={(e) => {
                    e.preventDefault();
                    handleRegimeChange('SIMPLES/MEI');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                    border:
                      selectedRegime === 'SIMPLES/MEI'
                        ? '2px solid var(--primary-color)'
                        : '1px solid #ccc',
                    borderRadius: 8,
                    cursor: 'pointer',
                    backgroundColor:
                      selectedRegime === 'SIMPLES/MEI'
                        ? 'var(--primary-color-light, #f0f7ff)'
                        : 'var(--background-input)',
                    fontWeight: 500,
                  }}
                >
                  MEI/SIMPLES
                </label>
              </div>
              <div className="col-6">
                <input
                  type="radio"
                  id="regime-real-presumido"
                  value="PRESUMIDO/REAL"
                  {...register('regimeTributacao', { required: true })}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="regime-real-presumido"
                  onClick={(e) => {
                    e.preventDefault();
                    handleRegimeChange('PRESUMIDO/REAL');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                    border:
                      selectedRegime === 'PRESUMIDO/REAL'
                        ? '2px solid var(--primary-color)'
                        : '1px solid #ccc',
                    borderRadius: 8,
                    cursor: 'pointer',
                    backgroundColor:
                      selectedRegime === 'PRESUMIDO/REAL'
                        ? 'var(--primary-color-light, #f0f7ff)'
                        : 'var(--background-input)',
                    fontWeight: 500,
                  }}
                >
                  REAL/PRESUMIDO
                </label>
              </div>
            </div>
            {errors.regimeTributacao && (
              <span style={{ color: 'rgb(235, 69, 69)', fontSize: 12 }}>
                Campo obrigatório
              </span>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-end mb-2">
              <a
                href={`https://oqapi-production.s3.us-east-1.amazonaws.com/Clients/1769445224599-24de555f-2c1f-420a-a41a-d0bc7291cf01`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--primary-color)', fontSize: 14 }}
              >
                <i
                  className="material-icons"
                  style={{
                    fontSize: 16,
                    verticalAlign: 'middle',
                    marginRight: 4,
                  }}
                >
                  description
                </i>
                Exemplo de Nota Fiscal
              </a>
            </div>

            {!taxInvoiceFileName ? (
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '30px 20px',
                  border: '2px dashed #ccc',
                  borderRadius: 8,
                  cursor: isUploadingNotaFiscal ? 'wait' : 'pointer',
                  backgroundColor: 'var(--background-input)',
                }}
              >
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleNotaFiscalUpload}
                  style={{ display: 'none' }}
                  disabled={isUploadingNotaFiscal}
                />
                <i
                  className="material-icons"
                  style={{ fontSize: 32, color: '#999', marginBottom: 8 }}
                >
                  cloud_upload
                </i>
                <span style={{ color: '#666' }}>
                  {isUploadingNotaFiscal
                    ? 'Enviando...'
                    : 'Anexar Nota Fiscal em PDF'}
                </span>
              </label>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '15px 20px',
                  border: '1px solid #28a745',
                  borderRadius: 8,
                  backgroundColor: '#f8fff8',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <i
                    className="material-icons"
                    style={{ fontSize: 24, color: '#28a745' }}
                  >
                    picture_as_pdf
                  </i>
                  <span>{taxInvoiceFileName}</span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveNotaFiscal}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                  }}
                >
                  <i className="material-icons" style={{ color: '#dc3545' }}>
                    delete
                  </i>
                </button>
              </div>
            )}
            {!taxInvoiceFileUrl && errors.notaNumero && (
              <span style={{ color: 'rgb(235, 69, 69)', fontSize: 12 }}>
                Anexo da nota fiscal é obrigatório
              </span>
            )}
          </div>
        </div>

        <hr className="my-4" />
      </div>
    );
  };

  const renderPaymentPix = () => {
    return (
      <>
        <div
          className="mt-3"
          style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
        >
          <img src={pixImage} alt="Pix Logo" width={20} />
          <span className="fs-4 fw-semibold">Selecione sua chave pix</span>
        </div>
        <div className="mt-3">
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <List>
                {client?.clientUrl !== GANHE_MAIS_CORRETOR_CLIENT_URL && (
                  <ListItem
                    handleChange={() => handleChange('CPF')}
                    icon="person"
                    content="CPF"
                    rangeChecked={typePix === 'CPF'}
                    withRange={true}
                  />
                )}
                <ListItem
                  handleChange={() => handleChange('CNPJ')}
                  icon="business"
                  content="CNPJ"
                  rangeChecked={typePix === 'CNPJ'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('TELEFONE')}
                  icon="phone"
                  content="Telefone"
                  rangeChecked={typePix === 'TELEFONE'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('EMAIL')}
                  icon="mail"
                  content="E-mail"
                  rangeChecked={typePix === 'EMAIL'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('CHAVE_ALEATORIA')}
                  icon="format_color_text"
                  content="Chave aleatória"
                  rangeChecked={typePix === 'CHAVE_ALEATORIA'}
                  withRange={true}
                />
              </List>
            </div>
            <div className="col-sm-12 col-md-6">
              <div className="my-3">
                <Input
                  maxLength={maxLength}
                  type="text"
                  label="Chave PIX"
                  placeholder={
                    typePix === 'CHAVE_ALEATORIA'
                      ? 'Copie e cole aqui'
                      : 'Digite sua chave Pix'
                  }
                  disabled={!typePix}
                  readOnly={!typePix}
                  {...register('pixkey', {
                    required: true,
                    pattern: {
                      value:
                        typePix === 'EMAIL'
                          ? /^[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,}$/i
                          : /./,
                      message:
                        typePix === 'EMAIL' ? 'invalid email address' : '',
                    },
                    validate: (value) => {
                      if (typePix === 'CNPJ') {
                        try {
                          if (!validateCNPJ(value)) return 'CNPJ inválido';
                        } catch {
                          return 'CNPJ inválido';
                        }
                      }
                      return true;
                    },
                  })}
                  errors={
                    errors.pixkey
                      ? errors.pixkey.type === 'pattern'
                        ? 'Email inválido'
                        : errors.pixkey.type === 'validate'
                          ? (errors.pixkey.message as string)
                          : 'Campo obrigatório'
                      : ''
                  }
                  onChange={(event) => {
                    const { value } = event.target;
                    if (typePix === 'CPF') {
                      if (value.length <= 14) {
                        event.target.value = MaskCPF(value);
                      }
                    }
                    if (typePix === 'CNPJ') {
                      event.target.value = MaskCNPJ(value);
                    }
                    if (typePix === 'TELEFONE') {
                      event.target.value = maskTEL(value);
                    }
                    if (typePix === 'CHAVE_ALEATORIA') {
                      event.target.value = value;
                    }
                    if (typePix === 'EMAIL') {
                      event.target.value = value.toLowerCase();
                    }
                  }}
                />
              </div>
              <div className="mt-3">
                <div className="alert alert-warning" role="alert">
                  <h4 className="alert-heading">Atenção ao resgatar</h4>
                  <br />
                  <ul>
                    {!allowsPaymentToThirdParties && (
                      <>
                        <li>
                          Os dados de resgate via PIX ou TED, devem corresponder
                          ao mesmo CPF ou CNPJ do usuário cadastrado na
                          plataforma.
                        </li>
                        <li>
                          Não é permitido utilizar contas de terceiros para
                          realizar o resgate.
                        </li>
                      </>
                    )}
                    <li>
                      Informe seus dados de resgate{' '}
                      <strong>corretamente</strong> e revise-os antes de
                      finalizar o resgate.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderFormRescueVoucherOrPhisic2 = () => {
    return (
      <form onSubmit={handleForm} noValidate>
        <div className="row">
          <div className="col-sm-12">
            <Card>
              <>
                <div className="mb-3">
                  <span className="fs-4 fw-semibold">Você está resgatando</span>
                </div>
                {cart &&
                  cart.map((award: any) => (
                    <List>
                      <ListItem
                        image={award.premiumImage}
                        content={award.premiumName}
                        secondaryContent={
                          award.amount === 1
                            ? `${award.amount} item`
                            : `${award.amount} itens`
                        }
                      />
                    </List>
                  ))}

                <div className="mt-3 d-flex flex-row-reverse">
                  <Button
                    disabled={isSubmitting}
                    type="submit"
                    text="Finalizar resgate"
                    color="primary"
                    icon="check"
                  />
                  <div className="me-3">
                    <Button
                      type="button"
                      onClick={onHandleCancelRescue}
                      text="Cancelar"
                      color="flat"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </>
            </Card>
          </div>
        </div>
      </form>
    );
  };

  // Modal de confirmação de Pix
  const renderModalConfirmPix = () => {
    return (
      <Modal
        open={modalConfirmPixIsOpen}
        onClose={handleCloseConfirmModal}
        center
      >
        <div style={{ minWidth: '400px', padding: '20px' }}>
          <h2>Confirmar Chave Pix</h2>
          <div className="mt-4">
            <p className="fs-6">
              {allowsPaymentToThirdParties
                ? 'Confira se a chave Pix informada está correta antes de confirmar.'
                : 'O pagamento será feito apenas para contas cuja chave Pix pertença ao mesmo CPF/CNPJ cadastrado na plataforma.'}
            </p>
          </div>

          <div className="mt-4 d-flex gap-3 justify-content-end">
            <Button
              type="button"
              onClick={handleCloseConfirmModal}
              text="Corrigir"
              color="flat"
            />
            <Button
              type="button"
              onClick={handleConfirmPix}
              text="Confirmar"
              color="primary"
              disabled={isConfirmingRescue}
              isLoading={isConfirmingRescue}
            />
          </div>
        </div>
      </Modal>
    );
  };

  // Modal de confirmação de Regime Tributário
  const renderModalConfirmRegime = () => {
    return (
      <Modal
        open={modalConfirmRegimeIsOpen}
        onClose={handleCancelRegime}
        center
      >
        <div style={{ minWidth: '400px', padding: '20px' }}>
          <h2>Confirmar Regime Tributário</h2>
          <div className="mt-4">
            <p className="fs-6">
              Você selecionou o regime <strong>LUCRO REAL/PRESUMIDO</strong>.
            </p>
            <p className="fs-6">
              Tem certeza que deseja continuar com essa opção?
            </p>
          </div>

          <div className="mt-4 d-flex gap-3 justify-content-end">
            <Button
              type="button"
              onClick={handleCancelRegime}
              text="Cancelar"
              color="flat"
            />
            <Button
              type="button"
              onClick={handleConfirmRegime}
              text="Confirmar"
              color="primary"
            />
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <>
      <HelmetComponent
        clientLogoURL={client?.logoURL}
        companyName={client?.companyName}
        clientFavicon={client?.favIcon}
        primaryColor={client?.primaryColor}
        secondaryColor={client?.secondaryColor}
        tabTitle={'Confirmar resgate'}
      />
      <div className="container">
        <div className="row mb-3">
          <ButtonBack
            title="Confirmar/Finalizar resgate"
            onClick={leaveRescue}
          />
        </div>
        {!cart || cart.length === 0 ? (
          renderEmptyCart()
        ) : (
          <>
            {/* FORMULÁRIO FORMULÁRIO TIPO DINHEIRO  */}
            {isAwardMoney && renderFormRescueMoney()}

            {/* FORMULÁRIO DE PREMIOS FISICOS */}
            {!isAwardMoney && renderFormRescueVoucherOrPhisic2()}
          </>
        )}

        <ModalCongratsRescue
          modalCongratsIsOpen={modalCongratsIsOpen}
          clientNumberOfDaysToRedeem={
            client && client.numberOfDaysToRedeem
              ? `até ${client.numberOfDaysToRedeem} dias úteis`
              : 'alguns dias'
          }
        />

        {/* Modal de confirmação Pix */}
        {renderModalConfirmPix()}

        {/* Modal de confirmação Regime Tributário */}
        {renderModalConfirmRegime()}
      </div>
    </>
  );
}
